import { useEffect, useRef, type ReactNode } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router";
import { AppShell } from "./components/AppShell";
import { FullScreenSpinner } from "./components/bits";
import { api } from "./lib/api";
import { useAuth } from "./lib/auth";
import { ActivityPage } from "./pages/Activity";
import { AssetDetailPage } from "./pages/AssetDetail";
import {
  ForgotPasswordPage,
  GoogleCompletePage,
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
  TwoFactorPage,
  VerifyEmailPage,
} from "./pages/auth";
import { DepositPage } from "./pages/Deposit";
import { HelpPage } from "./pages/Help";
import { HomePage } from "./pages/Home";
import { ConfirmWithdrawalPage, NotFoundPage } from "./pages/misc";
import { VaultPage } from "./pages/Vault";
import { VaultReceivePage } from "./pages/VaultReceive";
import { VaultSendPage } from "./pages/VaultSend";
import { VaultRecoverPage } from "./pages/VaultRecover";
import { VaultCreatePage, VaultImportPage } from "./pages/VaultSetup";
import { SwapPage } from "./pages/Swap";
import { SettingsPage } from "./pages/Settings";
import { WithdrawPage } from "./pages/Withdraw";

/** On first load, try to resume the session from the httpOnly refresh cookie. */
function useRestoreSession() {
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api
      .restoreSession()
      .then((ok) => {
        if (!ok) useAuth.getState().setAnonymous();
      })
      .catch(() => {
        useAuth.getState().setAnonymous();
        useAuth
          .getState()
          .setNotice("Can't reach the server right now. Check that the API is running, then reload.");
      });
  }, []);
}

function RequireAuth() {
  const status = useAuth((s) => s.status);
  const location = useLocation();
  if (status === "loading") return <FullScreenSpinner />;
  if (status === "anonymous")
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

function GuestOnly({ children }: { children?: ReactNode }) {
  const status = useAuth((s) => s.status);
  const location = useLocation();
  if (status === "loading") return <FullScreenSpinner />;
  if (status === "authenticated") {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from.startsWith("/") ? from : "/"} replace />;
  }
  return children ?? <Outlet />;
}

export function App() {
  useRestoreSession();
  return (
    <Routes>
      {/* Email links work whether or not you're logged in. */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/confirm-withdrawal" element={<ConfirmWithdrawalPage />} />
      <Route path="/auth/google/complete" element={<GoogleCompletePage />} />

      <Route element={<GuestOnly />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/2fa" element={<TwoFactorPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      </Route>

      <Route element={<RequireAuth />}>
        <Route index element={<HomePage />} />
        <Route path="/assets/:chain/:asset" element={<AssetDetailPage />} />
        <Route path="/deposit" element={<DepositPage />} />
        <Route path="/withdraw" element={<WithdrawPage />} />
        <Route path="/activity" element={<ActivityPage />} />
        <Route path="/swap" element={<SwapPage />} />
        <Route path="/vault" element={<VaultPage />} />
        <Route path="/vault/create" element={<VaultCreatePage />} />
        <Route path="/vault/import" element={<VaultImportPage />} />
        <Route path="/vault/receive" element={<VaultReceivePage />} />
        <Route path="/vault/send" element={<VaultSendPage />} />
        <Route path="/vault/recover" element={<VaultRecoverPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
